import { useCallback, useState } from 'react'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'
import { toApiError } from 'utility/crm/api-error'
import { canDeleteCrmRecords } from 'utility/crm/permissions'

/**
 * A value remembered per user in localStorage. Storage can throw (private mode, blocked
 * site data), so every read and write is wrapped and the page works without it.
 * @template T
 * @param {string} name
 * @param {T} fallback
 * @returns {[T, (next: T) => void]}
 */
export const usePreference = (name, fallback) => {
    const { user } = useAuth()
    const key = `eauditor.crm.${user?.unique_id || 'anon'}.${name}`
    const [value, setValue] = useState(() => {
        try {
            const stored = window.localStorage.getItem(key)
            return stored === null ? fallback : JSON.parse(stored)
        } catch {
            return fallback
        }
    })
    const update = useCallback(
        next => {
            setValue(next)
            try {
                window.localStorage.setItem(key, JSON.stringify(next))
            } catch {
                // storage unavailable: the choice just won't persist
            }
        },
        [key],
    )
    return [value, update]
}

/** True when the signed-in user is an admin (controls the Delete buttons). */
export const useIsCrmAdmin = () => {
    const { user } = useAuth()
    return canDeleteCrmRecords(user)
}

/** Toast helpers that turn a thrown CRM error into user-facing text. */
export const useCrmToast = () => {
    const { successToast, errorToast } = useNotification()
    const showError = useCallback(err => errorToast(toApiError(err).message), [errorToast])
    return { successToast, errorToast, showError }
}
